import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent, fireDualConfetti } from '@t-one-internal/design-system'
import { Button } from '@t-one-internal/design-system'
import { PaperPlaneTilt } from '@phosphor-icons/react'
import { useRecognitionStore } from '../../stores/recognitionStore'
import { useRecognitionData } from '../../hooks/useRecognitionData'
import RecognitionFeed from './components/RecognitionFeed'
import LeaderboardView from './components/LeaderboardView'
import GiveRecognitionDialog from './components/give-recognition/GiveRecognitionDialog'
import { AuditHistoryButton } from '../../components/audit/AuditHistoryModal'
import apiClient from '../../api/apiClient'

// Error boundary to prevent the whole page from going blank
class RecognitionErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RecognitionPage] Render error:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <div className="text-center">
            <p className="text-lg font-medium text-foreground mb-2">Something went wrong</p>
            <p className="text-sm text-muted-foreground mb-4">
              The recognition page encountered an error. Please try refreshing.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              Try Again
            </Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const RecognitionPageContent: React.FC = () => {
  useRecognitionData()

  const isGiveDialogOpen = useRecognitionStore((s) => s.isGiveDialogOpen)
  const openGiveDialog = useRecognitionStore((s) => s.openGiveDialog)
  const closeGiveDialog = useRecognitionStore((s) => s.closeGiveDialog)
  const setHighlightedRecognitionId = useRecognitionStore((s) => s.setHighlightedRecognitionId)

  const [searchParams, setSearchParams] = useSearchParams()
  const notificationId = searchParams.get('notification_id')
  const recognitionId = searchParams.get('recognition_id')
  const activeTab = searchParams.get('tab') || 'feed'

  useEffect(() => {
    const triggerCelebration = (notifId: string | number, recId?: string | number) => {
      // Mark the notification as read
      apiClient.put(`/user-notifications/${notifId}/read`).catch(console.error)
      // Refresh NotificationBell unread count
      window.dispatchEvent(new Event('notification-read'))

      // 🎉 dual-edge confetti burst
      fireDualConfetti()

      // Highlight the specific recognition card
      if (recId) {
        setHighlightedRecognitionId(String(recId))
      }
    }

    if (notificationId) {
      // Triggered by email or bell click
      triggerCelebration(notificationId, recognitionId || undefined)
    } else {
      // Direct visit -> check for any unread recognition notifications
      apiClient.get('/user-notifications/', { params: { limit: 20 } })
        .then((res: any) => {
          const unreadRec = res.data?.data?.find((n: any) => n.category === 'recognition' && !n.is_read)
          if (unreadRec) {
            triggerCelebration(unreadRec.id, unreadRec.reference_id ?? unreadRec.recognition_id)
          }
        })
        .catch(console.error)
    }

    // Clear highlight when leaving the page
    return () => {
      setHighlightedRecognitionId(null)
    }
  }, [notificationId, recognitionId, setHighlightedRecognitionId])

  return (
    <div>
      <Tabs value={activeTab} onValueChange={(value: string) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.set('tab', value)
          return next
        }, { replace: true })
      }}>
        {/* Tab header — sticky so it stays visible while the feed scrolls */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 border-b bg-background">
          <TabsList className="bg-muted">
            <TabsTrigger value="feed">Recognition Feed</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <AuditHistoryButton
              entityType="recognition"
              title="Recognition History"
              tooltip="View Recognition Audit Trail"
            />
            <Button size="default" className="gap-2 px-5 text-sm" onClick={openGiveDialog}>
              <PaperPlaneTilt size={18} weight="fill" />
              Give Kudos
            </Button>
          </div>
        </div>

        {/* Tab content — natural flow, outer main.overflow-y-auto handles scroll */}
        <TabsContent value="feed" className="mt-0">
          <RecognitionFeed />
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-0">
          <LeaderboardView />
        </TabsContent>

      </Tabs>

      <GiveRecognitionDialog
        open={isGiveDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeGiveDialog()
        }}
      />
    </div>
  )
}

const RecognitionPage: React.FC = () => (
  <RecognitionErrorBoundary>
    <RecognitionPageContent />
  </RecognitionErrorBoundary>
)

export default RecognitionPage
